/**
 * useBoardEvents: SSE hook for real-time board updates.
 *
 * Subscribes to SSE streams for all active (in_progress) tasks.
 * When any task status changes from any source (agent, CLI, another user),
 * the callback fires to trigger a data refresh.
 *
 * Falls back to 30s polling when SSE connections drop.
 */

import { useEffect, useRef, useCallback } from 'react';
import api from '@/api/engine';
import type { TaskItem, TaskEvent } from '@/api/types';

function parseSsePayload(raw: string): TaskEvent | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed) as TaskEvent;
  } catch {
    try {
      const coerced = trimmed
        .replace(/'/g, '"')
        .replace(/\bNone\b/g, 'null')
        .replace(/\bTrue\b/g, 'true')
        .replace(/\bFalse\b/g, 'false');
      return JSON.parse(coerced) as TaskEvent;
    } catch {
      return null;
    }
  }
}

// Event kinds that indicate a status change worth refreshing for
const REFRESH_KINDS = new Set([
  'task_dispatched',
  'task_status_changed',
  'task_completed',
  'task_held_for_review',
  'task_blocked',
  'task_cancelled',
  'task_stopped',
]);

interface UseBoardEventsOptions {
  tasks: TaskItem[];
  onRefresh: () => void;
  enabled?: boolean;
}

export function useBoardEvents({ tasks, onRefresh, enabled = true }: UseBoardEventsOptions) {
  const sourcesRef = useRef<Map<string, EventSource>>(new Map());
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce refresh to avoid hammering the API on burst events
  const debouncedRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      onRefresh();
    }, 500);
  }, [onRefresh]);

  useEffect(() => {
    if (!enabled) return;

    const activeTasks = tasks.filter(
      (t) => t.status === 'in_progress' || t.status === 'review',
    );

    const currentSources = sourcesRef.current;
    const activeIds = new Set(activeTasks.map((t) => t.id));

    // Close connections for tasks no longer active
    for (const [id, source] of currentSources.entries()) {
      if (!activeIds.has(id)) {
        source.close();
        currentSources.delete(id);
      }
    }

    // Open connections for newly active tasks
    for (const task of activeTasks) {
      if (currentSources.has(task.id)) continue;

      try {
        const source = api.tasks.subscribeEvents(task.id);

        source.onmessage = (ev) => {
          const parsed = parseSsePayload(ev.data as string);
          if (parsed && REFRESH_KINDS.has(parsed.kind)) {
            debouncedRefresh();
          }
        };

        source.onerror = () => {
          // EventSource auto-retries; we just note the disconnection
        };

        currentSources.set(task.id, source);
      } catch {
        // Non-fatal: SSE unavailable for this task
      }
    }

    return () => {
      // Don't close on every render — only on unmount
    };
  }, [tasks, enabled, debouncedRefresh]);

  // Cleanup all on unmount
  useEffect(() => {
    return () => {
      for (const source of sourcesRef.current.values()) {
        source.close();
      }
      sourcesRef.current.clear();
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  // Polling fallback: refresh every 30s
  useEffect(() => {
    if (!enabled) return;
    const interval = setInterval(onRefresh, 30000);
    return () => clearInterval(interval);
  }, [enabled, onRefresh]);
}
